import React from "react";
import { ShippingDetailsDto } from "../../../../api";
import { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import {
  GridIconRenderer,
  GridIconRendererProps
} from "../../../../framework/components/grid/GridIconRenderer";
import moment from "moment";
import { getValidDateFormat } from "../../../../utilities/dateUtils";
import { Grid, createHeaderAndHeaderTooltip } from "../../../../framework/components/grid/Grid";
import {
  GridLinkRenderer,
  GridLinkRendererProps
} from "../../../../framework/components/grid/GridLinkRenderer";
import { useTranslation } from "react-i18next";
import { ShippingDetailDocumentTypes } from "../../../../api/models/ShippingDetailDocumentTypes";

export interface ShippingDetailsGridProps {
  hasOrderTrackingRights: boolean;
  rowData: Array<ShippingDetailsDto> | undefined;
  downloadShippingDetailFile: (
    shippingNumber: string,
    orderAcknowledgementNumber: string,
    documentTypes: ShippingDetailDocumentTypes[]
  ) => void;
}

export const ShippingDetailsGrid: React.FunctionComponent<ShippingDetailsGridProps> = (props) => {
  const { t } = useTranslation();
  const shippingDetailsColumnDefs: ColDef[] = [
    {
      field: "shippingNumber",
      ...createHeaderAndHeaderTooltip(t("Shipping number"))
    },
    {
      field: "invoiceNumber",
      ...createHeaderAndHeaderTooltip(t("Invoice number"))
    },
    {
      field: "shippedQuantity",
      ...createHeaderAndHeaderTooltip(t("Qty shipped")),
      maxWidth: 125
    },
    {
      field: "shipped",
      ...createHeaderAndHeaderTooltip(t("Shipping date")),
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return moment(params.value).format(getValidDateFormat());
        }
        return "";
      },
      maxWidth: 150,
      minWidth: 120
    },
    {
      ...createHeaderAndHeaderTooltip(t("Packing list")),
      field: "packingListDocumentsAvailable",
      headerClass: "grid-header-center-with-menu",
      hide: !props.hasOrderTrackingRights,
      cellRenderer: GridIconRenderer,
      cellRendererParams: (params: ICellRendererParams) => {
        const shippingDetail = params.data as ShippingDetailsDto;
        const cellRendererParams: GridIconRendererProps = {
          ...params,
          showIcon: shippingDetail.packingListDocumentsAvailable,
          onClick: () => {
            props.downloadShippingDetailFile(
              shippingDetail.shippingNumber,
              shippingDetail.orderAcknowledgementNumber,
              [
                ShippingDetailDocumentTypes.PackingList,
                ShippingDetailDocumentTypes.PackingListService
              ]
            );
          },
          icon: "abb/download"
        };
        return cellRendererParams;
      }
    },
    {
      ...createHeaderAndHeaderTooltip(t("Packing details")),
      field: "packingDetailsDocumentsAvailable",
      headerClass: "grid-header-center-with-menu",
      hide: !props.hasOrderTrackingRights,
      cellRenderer: GridIconRenderer,
      cellRendererParams: (params: ICellRendererParams) => {
        const shippingDetail = params.data as ShippingDetailsDto;
        const cellRendererParams: GridIconRendererProps = {
          ...params,
          showIcon: shippingDetail.packingDetailsDocumentsAvailable,

          onClick: () => {
            props.downloadShippingDetailFile(
              shippingDetail.shippingNumber,
              shippingDetail.orderAcknowledgementNumber,
              [ShippingDetailDocumentTypes.PackingDetails]
            );
          },
          icon: "abb/download"
        };
        return cellRendererParams;
      }
    },
    {
      field: "forwarder",
      ...createHeaderAndHeaderTooltip(t("Forwarder"))
    },
    {
      field: "trackingNumber",
      ...createHeaderAndHeaderTooltip(t("Tracking number")),
      cellRenderer: GridLinkRenderer,
      cellRendererParams: (params: ICellRendererParams) => {
        const shippingDetail = params.data as ShippingDetailsDto;
        const cellRendererParams: GridLinkRendererProps = {
          ...params,
          text: shippingDetail.trackingNumber ?? "",
          link: shippingDetail.trackingLink
        };
        return cellRendererParams;
      }
    }
  ];

  return (
    <Grid
      onFirstDataRendered={(params) => {
        const columns = params.columnApi.getAllColumns();
        if (columns) {
          params.api.sizeColumnsToFit();
        }
      }}
      columnDefs={shippingDetailsColumnDefs}
      rowData={props.rowData ?? []}
      title={t("Shipping details")}
      titleHeadingLevel={3}
      domLayout="autoHeight"
      t={t}
    />
  );
};
